import { useState, useEffect, useMemo } from 'react';

import {
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Card,
  CardContent,
  InputBase,
  useTheme,
} from '@mui/material';

import { Cancel, Send, SkipNext, SkipPrevious } from '@mui/icons-material';

import AudioModal from '../AudioModal';
import { Document, Page, pdfjs } from 'react-pdf';
import { getStyled } from './styles';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

const MessageUploadMedias = ({
  isOpen,
  files,
  onClose,
  onSend,
  onCancelSelection,
}) => {
  const theme = useTheme();
  const classes = getStyled(theme);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [captions, setCaptions] = useState(files.map(() => ''));
  const [currentFileUrl, setCurrentFileUrl] = useState('');

  useEffect(() => {
    const setFile = async () => {
      const url = URL.createObjectURL(files[currentIndex]);
      setCurrentFileUrl(url);

      return () => {
        URL.revokeObjectURL(url);
      };
    };

    setFile();
  }, [files, currentIndex]);

  const handleClose = () => onClose();
  const handlePrev = () => {
    if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
  };
  const handleNext = () => {
    if (currentIndex < files.length - 1) setCurrentIndex(currentIndex + 1);
  };

  const handleCaptionChange = (e) => {
    const value = e.target.value;
    setCaptions((prev) => {
      const updated = [...prev];
      updated[currentIndex] = value;
      return updated;
    });
  };

  const handleSend = () => {
    const selectedMedias = files.map((file, index) => ({
      file,
      caption: captions[index],
    }));
    onSend(selectedMedias);
    handleClose();
  };

  const renderFileContent = useMemo(() => {
    if (!currentFileUrl) return null;

    const currentFile = files[currentIndex];

    const previewWrapper = (content) => (
      <>
        <div
          style={{
            ...classes.modal,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '400px',
          }}
        >
          {content}
        </div>
        <div
          style={{
            ...classes.modal,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography variant='h6'>{currentFile.name}</Typography>
        </div>
      </>
    );

    if (currentFile.type.startsWith('image')) {
      return previewWrapper(
        <img
          alt={`Imagem ${currentIndex + 1}`}
          src={currentFileUrl}
          style={{ maxWidth: '600px', maxHeight: '400px' }}
        />,
      );
    } else if (currentFile.type === 'application/pdf') {
      return previewWrapper(
        <div
          style={{
            width: '300px',
            height: '400px',
            overflow: 'hidden',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Document
            file={currentFileUrl}
            loading={<Typography>Carregando PDF...</Typography>}
          >
            <Page
              pageNumber={1}
              width={300}
              renderTextLayer={false}
              renderAnnotationLayer={false}
            />
          </Document>
        </div>,
      );
    } else if (currentFile.type.startsWith('video')) {
      return previewWrapper(
        <video
          src={currentFileUrl}
          controls
          style={{ maxWidth: '600px', maxHeight: '400px' }}
        />,
      );
    } else if (currentFile.type.startsWith('audio')) {
      return previewWrapper(<AudioModal url={currentFileUrl} />);
    } else {
      return (
        <div
          style={{
            ...classes.modal,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '400px',
          }}
        >
          <CardContent style={classes.modal}>
            <Typography variant='h6'>Visualização não disponível</Typography>
            <Typography variant='h6'>{currentFile.name}</Typography>
          </CardContent>
        </div>
      );
    }
  }, [currentIndex, currentFileUrl, classes, files]);

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && event.shiftKey) return;
    switch (event.key) {
      case 'Escape':
        onCancelSelection();
        break;
      case 'Enter':
        handleSend();
        break;
      case 'ArrowRight':
        handleNext();
        break;
      case 'ArrowLeft':
        handlePrev();
        break;
      default:
        break;
    }
  };

  return (
    <div style={classes.modal}>
      <Dialog open={isOpen} fullWidth maxWidth='md' scroll='paper'>
        <DialogContent sx={classes.modal}>
          <Card>
            {renderFileContent}
            <CardContent style={classes.modal}>
              <div style={classes.messageInputWrapperPrivate}>
                <InputBase
                  placeholder='Legenda (opcional)'
                  fullWidth
                  multiline
                  minRows={1}
                  maxRows={5}
                  value={captions[currentIndex]}
                  onChange={handleCaptionChange}
                  autoFocus
                  onKeyDown={handleKeyDown}
                  sx={{ padding: '4px 10px' }}
                />
              </div>
            </CardContent>
          </Card>
        </DialogContent>
        <DialogActions style={classes.modal}>
          <IconButton onClick={handlePrev} disabled={currentIndex === 0}>
            <SkipPrevious
              style={
                currentIndex === 0
                  ? classes.buttonDisable
                  : classes.buttonEnable
              }
            />
          </IconButton>
          <IconButton onClick={onCancelSelection}>
            <Cancel style={classes.buttonEnable} />
          </IconButton>
          <IconButton onClick={handleSend}>
            <Send style={classes.buttonEnable} />
          </IconButton>
          <IconButton
            onClick={handleNext}
            disabled={currentIndex === files.length - 1}
          >
            <SkipNext
              style={
                currentIndex === files.length - 1
                  ? classes.buttonDisable
                  : classes.buttonEnable
              }
            />
          </IconButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default MessageUploadMedias;
